import { useEffect, useState } from 'react';
import { CircularProgress, FormHelperText, Grid, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, ToggleButton, ToggleButtonGroup } from '@mui/material';
import InputMask from 'react-input-mask';
import BusinessIcon from '@mui/icons-material/Business';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { useFeedback } from '../../../../hooks/use-feedback';
import LocationAppService from '../../../../application/platform/base/location-app-service';
import { ICityResponse, ICountryResponse, IStateResponse } from '../../../../model/base/location-response';
import {
    ISupplierAddRequest, ISupplierAddressData, ISupplierContactData, ISupplierLegalPersonData, ISupplierPhysicalPersonData
} from '../../../../model/sge/supplier/request/supplier-add-edit-request';
import { ISupplierResponse } from '../../../../model/sge/supplier/response/supplier-response';
import { formatDocument } from '../../../../utils/document';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { addressFromZipCode, isPhysicalPerson, normalizeZipCode, toSupplierForm, validateSupplier } from './supplier-form-utils';

interface SupplierDrawerProps {
    open: boolean;
    initial?: ISupplierResponse;
    countries: ICountryResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: ISupplierAddRequest) => void;
}

export function SupplierDrawer({ open, initial, countries, isSaving, onClose, onSave }: SupplierDrawerProps) {
    const { showError } = useFeedback();
    const [form, setForm] = useState<ISupplierAddRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);
    const [states, setStates] = useState<IStateResponse[]>([]);
    const [cities, setCities] = useState<ICityResponse[]>([]);
    const isEdit = !!initial;
    const isPF = isPhysicalPerson(form);
    const validation = validateSupplier(form);
    const errors = showValidationErrors ? validation : {};
    const countryId = form.address?.countryId;
    const stateId = form.address?.stateId;

    useEffect(() => {
        if (!open) return;
        setForm(toSupplierForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    useEffect(() => {
        if (!countryId) {
            setStates([]);
            return;
        }
        LocationAppService.listStates(countryId)
            .then(setStates)
            .catch((error: any) => {
                setStates([]);
                showError('Não foi possível carregar os estados', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryId]);

    useEffect(() => {
        if (!stateId) {
            setCities([]);
            return;
        }
        LocationAppService.listCities(stateId)
            .then(setCities)
            .catch((error: any) => {
                setCities([]);
                showError('Não foi possível carregar os municípios', error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateId]);

    function update(patch: Partial<ISupplierAddRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function updateAddress(patch: Partial<ISupplierAddressData>) {
        setForm((current) => ({ ...current, address: { ...current.address, ...patch } }));
    }

    function updateContact(patch: Partial<ISupplierContactData>) {
        setForm((current) => ({ ...current, contact: { ...current.contact, ...patch } }));
    }

    function updatePhysicalPerson(patch: Partial<ISupplierPhysicalPersonData>) {
        setForm((current) => ({ ...current, physicalPerson: { ...current.physicalPerson, ...patch } }));
    }

    function updateLegalPerson(patch: Partial<ISupplierLegalPersonData>) {
        setForm((current) => ({ ...current, legalPerson: { ...current.legalPerson, ...patch } }));
    }

    async function handleZipCodeBlur(value: string) {
        const zipCode = normalizeZipCode(value);
        if (!zipCode) return;
        setIsLoadingZipCode(true);
        try {
            const address = addressFromZipCode(await LocationAppService.lookupZipCode(zipCode));
            if (address) updateAddress(address);
        } catch {
            // CEP não encontrado: o usuário preenche o endereço manualmente
        } finally {
            setIsLoadingZipCode(false);
        }
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, name: form.name!.trim() });
    }

    const documentNumber = initial?.cpf || initial?.cnpj;

    return (
        <FormDrawer
            open={open}
            icon={<BusinessIcon />}
            title={isEdit ? 'Editar fornecedor' : 'Novo fornecedor'}
            subtitle={isEdit ? `${initial?.personName || ''}${documentNumber ? ` · ${formatDocument(documentNumber)}` : ''}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar fornecedor'}
            isSaving={isSaving}
            saveDisabled={isLoadingZipCode}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Tipo de pessoa</InputLabel>
                        <ToggleButtonGroup
                            value={isPF ? 'PF' : 'PJ'}
                            exclusive
                            onChange={(_, value) => value && update({ supplierPersonType: value })}
                            disabled={isEdit}
                            sx={{ width: '100%' }}
                        >
                            <ToggleButton value="PF" sx={{ flex: 1 }}>Pessoa Física</ToggleButton>
                            <ToggleButton value="PJ" sx={{ flex: 1 }}>Pessoa Jurídica</ToggleButton>
                        </ToggleButtonGroup>
                        {isEdit && <FormHelperText>O tipo de pessoa não pode ser alterado após o cadastro</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>{isPF ? 'Nome' : 'Razão social'}</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.name || ''} onChange={(e) => update({ name: e.target.value })} error={!!errors.name} />
                        <FieldError message={errors.name} />
                    </Grid>
                    {isPF ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <InputLabel required>CPF</InputLabel>
                                <InputMask mask="999.999.999-99" value={form.physicalPerson?.cpf || ''} onChange={(e) => updatePhysicalPerson({ cpf: e.target.value })}>
                                    <OutlinedInput fullWidth autoComplete="off" error={!!errors.document} />
                                </InputMask>
                                <FieldError message={errors.document} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel>RG</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" value={form.physicalPerson?.rg || ''} onChange={(e) => updatePhysicalPerson({ rg: e.target.value })} />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <InputLabel required>CNPJ</InputLabel>
                                <InputMask mask="99.999.999/9999-99" value={form.legalPerson?.cnpj || ''} onChange={(e) => updateLegalPerson({ cnpj: e.target.value })}>
                                    <OutlinedInput fullWidth autoComplete="off" error={!!errors.document} />
                                </InputMask>
                                <FieldError message={errors.document} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel>Nome fantasia</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" value={form.legalPerson?.tradeName || ''} onChange={(e) => updateLegalPerson({ tradeName: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel>Inscrição estadual</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" value={form.legalPerson?.stateRegistration || ''} onChange={(e) => updateLegalPerson({ stateRegistration: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel>Inscrição municipal</InputLabel>
                                <OutlinedInput fullWidth autoComplete="off" value={form.legalPerson?.municipalRegistration || ''} onChange={(e) => updateLegalPerson({ municipalRegistration: e.target.value })} />
                            </Grid>
                        </>
                    )}
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Endereço" description="Informe o CEP para preencher o endereço automaticamente">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>CEP</InputLabel>
                        <InputMask
                            mask="99999-999"
                            value={form.address?.zipCode || ''}
                            onChange={(e) => updateAddress({ zipCode: e.target.value })}
                            onBlur={(e) => handleZipCodeBlur(e.target.value)}
                        >
                            <OutlinedInput
                                fullWidth
                                autoComplete="off"
                                error={!!errors.zipCode}
                                endAdornment={isLoadingZipCode ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null}
                            />
                        </InputMask>
                        <FieldError message={errors.zipCode} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>País</InputLabel>
                        <Select
                            fullWidth
                            value={form.address?.countryId ?? ''}
                            onChange={(e) => updateAddress({ countryId: parseNumberInput(e.target.value), stateId: undefined, municipalityId: undefined })}
                            disabled={isLoadingZipCode}
                            error={!!errors.countryId}
                        >
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {countries.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.countryId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Estado</InputLabel>
                        <Select
                            fullWidth
                            value={form.address?.stateId ?? ''}
                            onChange={(e) => updateAddress({ stateId: parseNumberInput(e.target.value), municipalityId: undefined })}
                            disabled={isLoadingZipCode || !countryId}
                            error={!!errors.stateId}
                        >
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {states.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.stateId} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Município</InputLabel>
                        <Select
                            fullWidth
                            value={form.address?.municipalityId ?? ''}
                            onChange={(e) => updateAddress({ municipalityId: parseNumberInput(e.target.value) })}
                            disabled={isLoadingZipCode || !stateId}
                            error={!!errors.municipalityId}
                        >
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {cities.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.municipalityId} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <InputLabel required>Logradouro</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.address?.street || ''} onChange={(e) => updateAddress({ street: e.target.value })} disabled={isLoadingZipCode} error={!!errors.street} />
                        <FieldError message={errors.street} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel required>Número</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.address?.number || ''} onChange={(e) => updateAddress({ number: e.target.value })} error={!!errors.number} />
                        <FieldError message={errors.number} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Complemento</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.address?.complement || ''} onChange={(e) => updateAddress({ complement: e.target.value })} disabled={isLoadingZipCode} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Bairro</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.address?.neighborhood || ''} onChange={(e) => updateAddress({ neighborhood: e.target.value })} disabled={isLoadingZipCode} error={!!errors.neighborhood} />
                        <FieldError message={errors.neighborhood} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Contato">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>E-mail</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" type="email" value={form.contact?.email || ''} onChange={(e) => updateContact({ email: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Telefone</InputLabel>
                        <InputMask mask="(99) 9999-9999" value={form.contact?.phone || ''} onChange={(e) => updateContact({ phone: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Celular</InputLabel>
                        <InputMask mask="(99) 99999-9999" value={form.contact?.mobile || ''} onChange={(e) => updateContact({ mobile: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Tel. comercial</InputLabel>
                        <InputMask mask="(99) 9999-9999" value={form.contact?.commercialPhone || ''} onChange={(e) => updateContact({ commercialPhone: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
